/**
 * RichTextEditor — TipTap-based rich-text editor for the Proposal
 * Narrative (and future rich-text fields).
 *
 * Design goals:
 *   • Familiar Word-style toolbar (bold, italic, underline, strike,
 *     H1/H2/H3, bullet + ordered lists, blockquote, link, HR, undo/
 *     redo) — no exotic tools that don't survive PDF rendering.
 *   • Word-paste-friendly. TipTap's StarterKit already strips MS
 *     Office wrapper elements; we additionally clamp headings to
 *     H1-H3 to match `reedosPrintStyles.js`.
 *   • Backward compatible. Legacy plain-text narratives are
 *     upgraded to `<p>` blocks on load so the editor state matches
 *     what the operator saw before.
 *   • Emits sanitized HTML through the `onChange(html)` callback.
 *     Backend re-sanitizes on write; this is user-input safety, not
 *     the storage boundary.
 */
import { useEffect, useCallback } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import {
    TextB, TextItalic, TextUnderline, TextStrikethrough,
    ListBullets, ListNumbers, Quotes, Link as LinkIcon,
    ArrowUUpLeft, ArrowUUpRight, Minus,
} from "@phosphor-icons/react";
import { toEditorContent } from "../lib/richText";
import { cn } from "../lib/utils";

function ToolbarButton({ active, disabled, onClick, title, testId, children }) {
    return (
        <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            disabled={disabled}
            title={title}
            aria-label={title}
            data-testid={testId}
            className={cn(
                "inline-flex items-center justify-center h-8 w-8 rounded border text-slate-700",
                "hover:bg-slate-100 hover:text-slate-900",
                "disabled:opacity-40 disabled:cursor-not-allowed",
                active
                    ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-800 hover:text-white"
                    : "bg-white border-slate-200",
            )}
        >
            {children}
        </button>
    );
}

function Divider() {
    return <span className="w-px h-6 bg-slate-200 mx-0.5" aria-hidden="true" />;
}

export default function RichTextEditor({
    value,
    onChange,
    placeholder = "Type here…",
    editable = true,
    minHeight = 200,
    testId = "rich-text-editor",
}) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // Clamp headings to H1-H3 — matches print styles.
                heading: { levels: [1, 2, 3] },
                // Ignore leftover Word wrapper markup on paste.
                codeBlock: { HTMLAttributes: { class: "code-block" } },
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: {
                    rel: "noopener noreferrer",
                    target: "_blank",
                },
            }),
        ],
        content: toEditorContent(value),
        editable,
        editorProps: {
            attributes: {
                class: cn(
                    "prose prose-sm max-w-none focus:outline-none px-4 py-3",
                    "prose-headings:font-display prose-p:my-2 prose-p:leading-relaxed",
                    "prose-strong:font-semibold prose-a:text-emerald-700",
                    "prose-ul:my-2 prose-ol:my-2 prose-li:my-1",
                    "prose-blockquote:border-l-4 prose-blockquote:border-slate-300 prose-blockquote:pl-3 prose-blockquote:text-slate-600",
                ),
                "data-testid": `${testId}-surface`,
                "data-placeholder": placeholder,
            },
        },
        onUpdate: ({ editor: ed }) => {
            const html = ed.getHTML();
            // TipTap emits `<p></p>` when the editor is cleared.
            // Normalize that to an empty string so downstream
            // consumers can use falsy checks.
            onChange?.(html === "<p></p>" ? "" : html);
        },
    });

    // Reconcile the editor content when the parent-controlled `value`
    // changes out-of-band (e.g. Cancel button restoring the original).
    useEffect(() => {
        if (!editor) return;
        const normalized = toEditorContent(value);
        if (normalized !== editor.getHTML()) {
            editor.commands.setContent(normalized, { emitUpdate: false });
        }
    }, [value, editor]);

    // Keep editable state in sync when the parent toggles it.
    useEffect(() => {
        if (editor && editor.isEditable !== editable) {
            editor.setEditable(editable);
        }
    }, [editable, editor]);

    const promptForLink = useCallback(() => {
        if (!editor) return;
        const previous = editor.getAttributes("link").href || "";
        const url = window.prompt("URL (leave blank to remove link):", previous);
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }, [editor]);

    if (!editor) return null;

    return (
        <div
            className={cn(
                "rounded-md border border-slate-300 bg-white overflow-hidden",
                "focus-within:ring-2 focus-within:ring-emerald-500/40 focus-within:border-emerald-500/60",
                !editable && "opacity-70",
            )}
            data-testid={testId}
        >
            {editable && (
                <div className="flex items-center flex-wrap gap-1 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
                    <ToolbarButton
                        title="Bold (Ctrl+B)"
                        testId={`${testId}-bold`}
                        active={editor.isActive("bold")}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                    >
                        <TextB size={16} weight="bold" />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Italic (Ctrl+I)"
                        testId={`${testId}-italic`}
                        active={editor.isActive("italic")}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                    >
                        <TextItalic size={16} />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Underline (Ctrl+U)"
                        testId={`${testId}-underline`}
                        active={editor.isActive("underline")}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                    >
                        <TextUnderline size={16} />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Strikethrough"
                        testId={`${testId}-strike`}
                        active={editor.isActive("strike")}
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                    >
                        <TextStrikethrough size={16} />
                    </ToolbarButton>
                    <Divider />
                    <ToolbarButton
                        title="Heading 1"
                        testId={`${testId}-h1`}
                        active={editor.isActive("heading", { level: 1 })}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    >
                        <span className="text-xs font-bold">H1</span>
                    </ToolbarButton>
                    <ToolbarButton
                        title="Heading 2"
                        testId={`${testId}-h2`}
                        active={editor.isActive("heading", { level: 2 })}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    >
                        <span className="text-xs font-bold">H2</span>
                    </ToolbarButton>
                    <ToolbarButton
                        title="Heading 3"
                        testId={`${testId}-h3`}
                        active={editor.isActive("heading", { level: 3 })}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    >
                        <span className="text-xs font-bold">H3</span>
                    </ToolbarButton>
                    <Divider />
                    <ToolbarButton
                        title="Bulleted list"
                        testId={`${testId}-ul`}
                        active={editor.isActive("bulletList")}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                    >
                        <ListBullets size={16} />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Numbered list"
                        testId={`${testId}-ol`}
                        active={editor.isActive("orderedList")}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    >
                        <ListNumbers size={16} />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Blockquote"
                        testId={`${testId}-blockquote`}
                        active={editor.isActive("blockquote")}
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    >
                        <Quotes size={16} />
                    </ToolbarButton>
                    <Divider />
                    <ToolbarButton
                        title="Link"
                        testId={`${testId}-link`}
                        active={editor.isActive("link")}
                        onClick={promptForLink}
                    >
                        <LinkIcon size={16} />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Horizontal rule"
                        testId={`${testId}-hr`}
                        onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    >
                        <Minus size={16} weight="bold" />
                    </ToolbarButton>
                    <Divider />
                    <ToolbarButton
                        title="Undo (Ctrl+Z)"
                        testId={`${testId}-undo`}
                        disabled={!editor.can().undo()}
                        onClick={() => editor.chain().focus().undo().run()}
                    >
                        <ArrowUUpLeft size={16} />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Redo (Ctrl+Shift+Z)"
                        testId={`${testId}-redo`}
                        disabled={!editor.can().redo()}
                        onClick={() => editor.chain().focus().redo().run()}
                    >
                        <ArrowUUpRight size={16} />
                    </ToolbarButton>
                </div>
            )}
            <EditorContent
                editor={editor}
                style={{ minHeight: `${minHeight}px` }}
                className="text-sm text-slate-800 [&_.ProseMirror]:min-h-[inherit] [&_.ProseMirror]:outline-none"
            />
        </div>
    );
}
